import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IServerError } from '@api/interfaces';
import { lastValueFrom } from '@app/util/rxjs-7-compat';
import { TranslateService } from '@ngx-translate/core';
import { snakeCase } from 'lodash';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Injectable({
	providedIn: 'root'
})
export class ErrorNotificationService {

	private static readonly DEFAULT_KEY = 'word.error';

	constructor(
		private notification: NzNotificationService,
		private translation: TranslateService,
	) { }

	/**
	 * Resolve the notification title.
	 *
	 * The method returns
	 * * the translated `title_key` if it is defined
	 * * the translated `error_title_key` if it is defined and the translation is defined
	 * * the translated `DEFAULT_KEY`
	 *
	 * @param title_key optional title key
	 * @param error_title_key optional error title key for translating error names
	 * @returns the translated title
	 */
	private async resolveTitle(title_key: string, error_title_key?: string): Promise<string> {
		if (title_key) {
			return await lastValueFrom(this.translation.get(title_key));
		}

		if (error_title_key) {
			const title = await lastValueFrom(this.translation.get(error_title_key));
			if (title !== error_title_key) {
				return title;
			}
		}

		return await lastValueFrom(this.translation.get(ErrorNotificationService.DEFAULT_KEY));
	}

	/**
	 * Show error notification for the error object
	 *
	 * Error object may be:
	 * * HttpErrorResponse with IServerError payload
	 * * Error object
	 * * value with toString() method
	 *
	 * @param error Error object
	 * @param title_key optional message title translation key
	 */
	public async showErrorNotification(error: unknown, title_key?: string): Promise<void> {
		try {
			if (error instanceof HttpErrorResponse && error.error) {
				const serverError = error.error as IServerError;
				const error_title_key = serverError.name && `server_error.${snakeCase(serverError.name)}`;
				const title = await this.resolveTitle(title_key, error_title_key);
				this.notification.error(title, serverError.message);
			} else if (error instanceof Error) {
				const error_title_key = error.name && `error.${snakeCase(error.name)}`;
				const title = await this.resolveTitle(title_key, error_title_key);
				this.notification.error(title, error.message);
			} else {
				const title = await this.resolveTitle(title_key);
				this.notification.error(title, error?.toString() || '');
			}
		} catch (err) {
			console.error(error);
		}
	}
}
